import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Alert, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import BetaFeedbackModal from '../components/BetaFeedbackModal';
import PlanChangeModal from '../components/PlanChangeModal';
import '../App.css';

const get_credits_bar_color = (used, total) => {
	if(total === 0) {
		return '#6B7280';
	}

	const pct = used / total;
	if(pct >= 0.8) {
		return '#EF4444';
	}

	if(pct >= 0.5) {
		return '#F59E0B';
	}

	return '#22C55E';
};

const format_plan_date = (value) => {
	if(!value) {
		return '';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

// The date a used-up upgrade becomes available again.
//
// Said as a date only while that date is still ahead. A renewal that arrived without a period end
// leaves the stored date behind where it was, and showing a day that has already passed as though
// it were a promise is worse than not naming one, so it falls back to naming the event instead.
const render_upgrade_locked_note = (unlocks_at) => {
	const unlock_date = unlocks_at ? new Date(unlocks_at) : null;
	const is_future = unlock_date && !isNaN(unlock_date.getTime()) && (unlock_date.getTime() > new Date().getTime());

	return (
		<p className="profile-plan-locked">
			{is_future ?
				('You have already upgraded this billing period. You can upgrade again from ' + format_plan_date(unlocks_at) + '.') :
				'You have already upgraded this billing period. You can upgrade again at your next renewal.'}
		</p>
	);
};

// A change the user has arranged that has not happened yet, and the way to call it off.
const render_pending_change = (subscription, on_cancel_pending, cancelling_pending) => {
	// Named as a date when there is one, and as the event otherwise. A row with no usable date
	// would otherwise read as a sentence with a gap where the day should be.
	const effective_date = format_plan_date(subscription.pending_effective_date);
	const when = effective_date ? ('on ' + effective_date) : 'at your next renewal';

	return (
		<div className="profile-plan-pending">
			<span className="profile-field-label">
				Changing to {subscription.pending_plan_name} {when}.
				You keep your current plan and credits until then.
			</span>
			<Button
				variant="outline-secondary"
				size="sm"
				className="profile-plan-cancel-btn"
				onClick={on_cancel_pending}
				disabled={cancelling_pending}>
				{cancelling_pending ? <Spinner size="sm" animation="border" /> : 'Cancel Change'}
			</Button>
		</div>
	);
};

const format_price = (price_in_cents) => {
	if(!price_in_cents && price_in_cents !== 0) {
		return '';
	}

	return '$' + (price_in_cents / 100).toFixed(2);
};

// Which way a change goes, decided on price alone. The same tier billed annually costs more than
// billed monthly, so switching interval is an upgrade or a downgrade like any other move.
const get_change_direction = (plan, current_plan) => {
	if(!current_plan || (plan.id === current_plan.id)) {
		return 'current';
	}

	if(plan.price_in_cents > current_plan.price_in_cents) {
		return 'upgrade';
	}

	if(plan.price_in_cents < current_plan.price_in_cents) {
		return 'downgrade';
	}

	return 'same_price';
};

// What choosing this plan would do, said on the option itself rather than waiting for the modal.
// An upgrade takes money today; a downgrade takes none and waits. Those are not the same decision
// and must not read the same.
const render_option_consequence = (direction, preview, period_end, is_locked) => {
	if(direction === 'current') {
		return <span className="profile-plan-option-note">Your current plan</span>;
	}

	// A change is already arranged, so nothing here can be acted on until it is dealt with. Saying
	// what it would cost would be describing an action the page will not take.
	if(is_locked) {
		return (
			<span className="profile-plan-option-note">
				Cancel the change above to move to this plan instead.
			</span>
		);
	}

	if(direction === 'same_price') {
		return <span className="profile-plan-option-note">Same price as your current plan</span>;
	}

	if(direction === 'upgrade') {
		// Priced from the preview when it has arrived. Until then the option says what kind of
		// change it is without naming a figure, rather than showing a guess.
		const amount = preview ? format_price(preview.amount_due_now) : '';

		return (
			<span className="profile-plan-option-note profile-plan-option-note-upgrade">
				{amount ?
					('Upgrade now. ' + amount + ' today, credits arrive immediately.') :
					'Upgrade now. Charged today, credits arrive immediately.'}
			</span>
		);
	}

	// Same date rules as the pending banner: a date while there is one, the event otherwise.
	const effective_date = format_plan_date(period_end);
	const when = effective_date ? ('Takes effect ' + effective_date + '.') : 'Takes effect at your next renewal.';

	return (
		<span className="profile-plan-option-note profile-plan-option-note-downgrade">
			{when + ' Nothing charged now, nothing refunded.'}
		</span>
	);
};

// One tier per option, filtered to the interval being looked at, with the current plan among them
// so the choice can be compared rather than reconstructed from a label further up the page.
const render_plan_options = (plans, subscription, current_plan, selected_interval, previews, plan_actions, is_locked) => {
	const interval_plans = plans.filter(function(plan) {
		return plan.billing_interval === selected_interval;
	});

	if(!interval_plans.length) {
		return null;
	}

	return (
		<div className="profile-plan-options">
			<span className="profile-field-label">Change plan</span>

			{/* Plain buttons rather than Bootstrap variants. The variant classes set their colours
			    through --bs-btn-* variables that .btn resolves at paint time, which overrode every
			    border rule written against the element itself. */}
			<div className="profile-plan-toggle">
				<button
					type="button"
					className={selected_interval === 'month' ? 'profile-plan-toggle-btn profile-plan-toggle-btn-on' : 'profile-plan-toggle-btn'}
					onClick={function() { plan_actions.on_select_interval('month'); }}>
					{Constants.get_billing_interval_label('month')}
				</button>
				<button
					type="button"
					className={selected_interval === 'year' ? 'profile-plan-toggle-btn profile-plan-toggle-btn-on' : 'profile-plan-toggle-btn'}
					onClick={function() { plan_actions.on_select_interval('year'); }}>
					{Constants.get_billing_interval_label('year')}
				</button>
			</div>

			<div className="profile-plan-option-list">
				{interval_plans.map(function(plan) {
					const direction = get_change_direction(plan, current_plan);
					const is_current = (direction === 'current');
					const preview = previews[plan.id];

					// The plan already being moved to says so, rather than offering itself as
					// somewhere else to go.
					const is_pending_target = (subscription.pending_plan_id === plan.id);

					return (
						<div
							key={plan.id}
							className={'profile-plan-option profile-plan-option-' + direction}>
							<div className="profile-plan-option-head">
								<span className="profile-plan-option-name">{plan.name}</span>
								<span className="profile-plan-option-price">
									{format_price(plan.price_in_cents) + ' / ' + Constants.get_billing_interval_label(plan.billing_interval)}
								</span>
							</div>

							{is_pending_target ? (
								<span className="profile-plan-option-note profile-plan-option-note-downgrade">
									{'You are moving to this plan ' +
										(format_plan_date(subscription.pending_effective_date) ?
											('on ' + format_plan_date(subscription.pending_effective_date)) :
											'at your next renewal') + '.'}
								</span>
							) : render_option_consequence(direction, preview, subscription.renewal_date, is_locked)}

							{/* Both changes are ordinary actions and read the same. What separates them
							    is the line above: an upgrade names an amount charged today, a
							    downgrade names a date. The plan already held is not a button. */}
							{is_current ? (
								<Button variant="secondary" size="sm" className="profile-plan-option-btn" disabled>
									Current Plan
								</Button>
							) : is_pending_target ? (
								<Button variant="secondary" size="sm" className="profile-plan-option-btn" disabled>
									Change Scheduled
								</Button>
							) : (
								<Button
									variant="primary"
									size="sm"
									className="profile-plan-option-btn"
									disabled={is_locked || (direction === 'same_price')}
									onClick={function() { plan_actions.on_select_plan(plan); }}>
									{direction === 'upgrade' ? 'Upgrade' : 'Schedule Downgrade'}
								</Button>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

const render_subscription_section = (plan_name, quota, subscription_features, navigate, subscription, plans, plan_actions) => {
	const features_list = subscription_features || [];
	const credits_feature = features_list.find(function(f) {
		return f.code === 'MONTHLY_TRAINING_CREDITS';
	});

	const total = credits_feature ? parseInt(credits_feature.limit_value || '0') : 0;
	const remaining = quota ? (quota.remaining != null ? quota.remaining : 0) : total;
	const used = Math.max(0, total - remaining);

	const pct_used = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
	const bar_color = get_credits_bar_color(used, total);

	return (
		<div className="profile-section">
			<h5 className="profile-section-title">Subscription</h5>

			{plan_name ? (
				<React.Fragment>
					<div className="profile-sub-plan-row">
						<span className="profile-field-label">Plan</span>
						<span className="profile-field-value profile-field-value-bold">{plan_name}</span>
					</div>

					{total > 0 && (
						<div className="profile-sub-credits">
							<div className="profile-sub-credits-header">
								<span className="profile-field-label">Training Credits</span>
								<span className="profile-sub-credits-count">
									{remaining.toLocaleString()} / {total.toLocaleString()} remaining
								</span>
							</div>
							<div className="profile-sub-credits-bar-track">
								<div
									className="profile-sub-credits-bar-fill"
									style={{
										width: pct_used + '%',
										backgroundColor: bar_color,
									}}>
								</div>
							</div>
							<span className="profile-sub-credits-pct">{pct_used}% used this month</span>
						</div>
					)}

					{/* Changing plan happens here rather than through the signup flow: the user
					    already has a subscription and a payment method, so this is a change to it
					    rather than a fresh purchase. */}
					{subscription && subscription.plan_id && plan_name !== 'Beta' && (
						<React.Fragment>
							{/* The banner sits above the options rather than replacing them. What is
						    changing and what is being left are the same decision, so hiding the
						    plans while a change is arranged takes away the comparison at the
						    moment it is most wanted. The options are shown, and locked. */}
						{subscription.pending_plan_name &&
							render_pending_change(subscription, plan_actions.on_cancel_pending, plan_actions.cancelling_pending)}

						{render_plan_options(plans, subscription, plan_actions.current_plan,
											 plan_actions.selected_interval, plan_actions.previews, plan_actions,
											 subscription.pending_plan_name ? true : false)}

							{subscription.upgraded_this_period && !subscription.pending_plan_name &&
								render_upgrade_locked_note(subscription.renewal_date)}
						</React.Fragment>
					)}
				</React.Fragment>
		) : (subscription && subscription.suspended) ? (
				// Held, not lost. Said plainly, and with no button offering to sell them a plan they
				// are already paying for: what they need is to fix the card, not to choose again.
				<div className="profile-sub-suspended">
					<span className="profile-field-label profile-sub-suspended-title">
						{subscription.suspended_plan_name ?
							('Your ' + subscription.suspended_plan_name + ' plan is on hold.') :
							'Your plan is on hold.'}
					</span>
					<span className="profile-field-label">
						A payment did not go through. Your plan and its credits stay as they are and
						come back as soon as a payment succeeds. Update the card on file with your
						bank or card provider, and the next attempt will restore your account.
					</span>
				</div>
			) : (
				<div className="profile-sub-no-plan">
					<span className="profile-field-label">No active subscription.</span>
					<Button
						variant="primary"
						size="sm"
						className="profile-sub-upgrade-btn"
						onClick={() => { navigate('/plans'); }}>
						View Plans
					</Button>
				</div>
			)}
		</div>
	);
};

const render_email_section = (email) => {
	return (
		<div className="profile-section">
			<h5 className="profile-section-title">Account</h5>
			<div className="profile-field">
				<span className="profile-field-label">Email</span>
				<span className="profile-field-value">{email}</span>
			</div>
		</div>
	);
};

function ProfileView() {
	const navigate = useNavigate();
	const { token, subscription_features } = useAuth();
	const email = localStorage.getItem(Constants.USER_EMAIL) || '';
	const user_id = localStorage.getItem(Constants.USER_ID);

	const [show_confirm, set_show_confirm] = useState(false);
	const [deleting, set_deleting] = useState(false);
	const [error, set_error] = useState('');
	const [quota, set_quota] = useState(null);
	const [plan_name, set_plan_name] = useState(null);
	const [loading_subscription, set_loading_subscription] = useState(true);
	const [default_voice, set_default_voice] = useState(
		localStorage.getItem(Constants.DEFAULT_VOICE_MODE) !== 'false'
	);
	const [show_beta_modal, set_show_beta_modal] = useState(false);

	// The subscription in full, and every plan that could be moved to. Held separately from
	// plan_name because the plan-change surface needs the pending change, the renewal date and
	// whether this period has already had its upgrade.
	const [subscription, set_subscription] = useState(null);
	const [plans, set_plans] = useState([]);
	const [selected_plan, set_selected_plan] = useState(null);
	const [show_plan_change, set_show_plan_change] = useState(false);
	const [cancelling_pending, set_cancelling_pending] = useState(false);
	const [reload_version, set_reload_version] = useState(0);

	// Which billing interval the options are being looked at. Starts on the one the user is already
	// billed on, so the first thing they see is a like-for-like comparison.
	const [selected_interval, set_selected_interval] = useState('month');

	// Prorated upgrade amounts, keyed by plan id. Held here rather than fetched per render: the
	// figure depends on how much of the period is left, which does not change between renders.
	const [previews, set_previews] = useState({});

	useEffect(function() {
		if(!user_id || !token) {
			set_loading_subscription(false);
			return;
		}

		const headers = { Authorization: 'Bearer ' + token };

		Promise.allSettled([
			axios.get(Constants.SERVER_URL + '/api/quota', { headers }),
			axios.get(Constants.SERVER_URL + '/api/user_subscription', { headers }),
			axios.get(Constants.SERVER_URL + '/api/plans', { headers }),
		]).then(function(results) {
			const quota_result = results[0];
			const sub_result = results[1];
			const plans_result = results[2];

			if(quota_result.status === 'fulfilled') {
				set_quota(quota_result.value.data);
			}

			if(plans_result.status === 'fulfilled') {
				const raw = plans_result.value.data;
				const all_plans = Array.isArray(raw) ? raw : (raw.plans || []);

				// Beta is not a plan anyone chooses, and an inactive plan is not on sale, so
				// neither is offered as a destination. The plan the user already holds stays
				// whether or not it is still on sale, because the current row and the direction of
				// every other option are worked out from it.
				const held_plan_id = (sub_result.status === 'fulfilled') ? sub_result.value.data.plan_id : null;
				set_plans(all_plans.filter(function(plan) {
					return (plan.name !== 'Beta') && (plan.is_active || (plan.id === held_plan_id));
				}));
			}

			if(sub_result.status === 'fulfilled') {
				const fetched_plan_name = sub_result.value.data.plan_name || null;
				set_plan_name(fetched_plan_name);
				set_subscription(sub_result.value.data);

				if(fetched_plan_name === 'Beta') {
					const shown_key = 'beta_feedback_modal_shown_' + user_id;
					const quota_exhausted = (quota_result.status === 'fulfilled') && (quota_result.value.data) && (quota_result.value.data.allowed === false);
					if((!localStorage.getItem(shown_key)) && quota_exhausted) {
						set_show_beta_modal(true);
					}
				}
			}

			set_loading_subscription(false);
		});
	}, [token, user_id, reload_version]);

	const current_plan = plans.find(function(plan) {
		return subscription && (plan.id === subscription.plan_id);
	});

	// Follows the plan the user is on, so the options open on a like-for-like comparison rather
	// than on whichever interval happens to be first.
	useEffect(function() {
		if(current_plan && current_plan.billing_interval) {
			set_selected_interval(current_plan.billing_interval);
		}
	}, [current_plan]);

	// One request per upgrade option, once, when the interval is first looked at.
	//
	// Only upgrades are priced: a downgrade is always nothing today, so asking would be a request
	// whose answer is already known. Results are kept by plan id and the effect skips any it
	// already holds, so switching back and forth costs nothing and a render costs nothing.
	useEffect(function() {
		if(!subscription || !current_plan || !token || !plans.length) {
			return;
		}

		if(subscription.pending_plan_name) {
			return;
		}

		const to_price = plans.filter(function(plan) {
			if(plan.billing_interval !== selected_interval) {
				return false;
			}

			if(plan.id === current_plan.id) {
				return false;
			}

			if(plan.price_in_cents <= current_plan.price_in_cents) {
				return false;
			}

			return !previews[plan.id];
		});

		if(!to_price.length) {
			return;
		}

		var stopped = false;
		const headers = { Authorization: 'Bearer ' + token };

		Promise.allSettled(to_price.map(function(plan) {
			const url = Constants.SERVER_URL + '/api/plan_change_preview?plan_id=' + plan.id;
			return axios.get(url, { headers });
		})).then(function(results) {
			if(stopped) {
				return;
			}

			const fetched = {};
			results.forEach(function(result, index) {
				if(result.status === 'fulfilled') {
					fetched[to_price[index].id] = result.value.data;
				}
			});

			if(Object.keys(fetched).length) {
				set_previews(function(existing) {
					return Object.assign({}, existing, fetched);
				});
			}
		});

		return function() {
			stopped = true;
		};
	}, [subscription, current_plan, plans, selected_interval, token, user_id, previews]);

	// Read back from the server after a change rather than patched locally, so what is shown is
	// what was actually recorded.
	const reload_subscription = function() {
		set_reload_version(reload_version + 1);
		set_previews({});
	};

	const on_select_interval = function(interval) {
		set_selected_interval(interval);
	};

	const on_select_plan = function(plan) {
		set_selected_plan(plan);
		set_show_plan_change(true);
	};

	const on_plan_changed = function() {
		set_show_plan_change(false);
		set_selected_plan(null);

		// Cleared before the reload rather than after. The change has already happened at this
		// point, so every price held here was worked out against a plan the user has left, and
		// asking for them again while the old subscription is still in state prices a move to the
		// plan they are now on. The server refuses that, correctly, with a 400.
		set_subscription(null);
		reload_subscription();
	};

	const on_cancel_pending = async function() {
		set_cancelling_pending(true);
		set_error('');

		try {
			const headers = { Authorization: 'Bearer ' + token };
			await axios.post(Constants.SERVER_URL + '/api/cancel_scheduled_change', {}, { headers });
			reload_subscription();
		} catch (error) {
			console.log('on_cancel_pending - ERROR: ', error);
			const message = error.response && error.response.data && error.response.data.message;
			set_error(message || 'Could not cancel the scheduled change.');
		} finally {
			set_cancelling_pending(false);
		}
	};

	const handle_beta_modal_close = function() {
		localStorage.setItem('beta_feedback_modal_shown_' + user_id, 'true');
		set_show_beta_modal(false);
	};

	const handle_delete = async () => {
		try {
			console.log('handle_delete...');
			set_deleting(true);
			set_error('');
			const headers = { Authorization: 'Bearer ' + token };
			await axios.delete(Constants.SERVER_URL + '/api/user/' + user_id, { headers });
			localStorage.removeItem(Constants.TOKEN_NAME);
			localStorage.removeItem(Constants.USER_EMAIL);
			localStorage.removeItem(Constants.USER_ID);
			localStorage.removeItem(Constants.DEFAULT_VOICE_MODE);
			window.location.replace('/login');
		} catch (error) {
			console.log('handle_delete - ERROR: ', error);
			const msg = error.response && error.response.data && error.response.data.message;
			set_error(msg || 'Failed to delete account. Please try again.');
			set_deleting(false);
		}
	};

	return (
		<React.Fragment>
			<main className="profile-main">
				<div className="profile-header">
					<h4 className="admin-title">Account</h4>
				</div>

				<div className="profile-card">
					{render_email_section(email)}

					{loading_subscription ? (
						<div className="profile-section">
							<Spinner animation="border" size="sm" />
						</div>
					) : (
						render_subscription_section(plan_name, quota, subscription_features, navigate,
													subscription, plans,
													{ on_select_plan, on_cancel_pending, cancelling_pending,
													  on_select_interval, selected_interval, previews, current_plan })
					)}

					<div className="profile-section">
						<h5 className="profile-section-title">Settings</h5>
						<Form.Check
							type="switch"
							id="default-voice-switch"
							label="Default to voice mode"
							checked={default_voice}
							onChange={function(e) {
								const val = e.target.checked;
								set_default_voice(val);
								localStorage.setItem(Constants.DEFAULT_VOICE_MODE, val ? 'true' : 'false');
							}}
							className="profile-setting-switch"
						/>
					</div>

					<div className="profile-section">
						<h5 className="profile-section-title">Password</h5>
						<Button
							variant="primary"
							onClick={() => { navigate('/request_password_reset', { state: { email } }); }}>
							Change Password
						</Button>
					</div>

					<div className="profile-section">
						<h5 className="profile-section-title">Danger Zone</h5>
						<p className="profile-field-label">Permanently delete your account and all associated data.</p>
						<Button
							variant="danger"
							onClick={() => { set_show_confirm(true); }}>
							Delete Account
						</Button>
					</div>
				</div>
			</main>

			<BetaFeedbackModal show={show_beta_modal} on_hide={handle_beta_modal_close} />

			<PlanChangeModal
				show={show_plan_change}
				plan={selected_plan}
				user_id={user_id}
				token={token}
				on_hide={function() { set_show_plan_change(false); set_selected_plan(null); }}
				on_changed={on_plan_changed}
			/>

			<Modal show={show_confirm} onHide={() => { set_show_confirm(false); }} centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete Account</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Are you sure you want to permanently delete your account? This cannot be undone.</p>
					{error && <Alert variant="danger">{error}</Alert>}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => { set_show_confirm(false); }} disabled={deleting}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handle_delete} disabled={deleting}>
						{deleting ? <Spinner size="sm" animation="border" /> : 'Delete My Account'}
					</Button>
				</Modal.Footer>
			</Modal>
		</React.Fragment>
	);
}

export default ProfileView;
